import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Upload, Search, ArrowRight, Sparkles } from "lucide-react";
import AppShell from "@/components/AppShell";
import MagicProspectButton from "@/components/MagicProspectButton";

/**
 * Trouver — page d'acquisition de prospects.
 *
 * Trois entrées simples, comme la page "New" d'Apollo :
 *   1. Sur la carte (géo-radar autour de moi)
 *   2. Importer une liste (CSV, Excel, vCard)
 *   3. Recherche par secteur (Pappers / SIRENE par filtre)
 *
 * Tout autre point d'entrée (Magic Prosperr enrichissement) reste dans la FAB.
 */
export default function Trouver() {
  const navigate = useNavigate();

  const methods = [
    {
      id: "maps",
      title: "Sur la carte",
      sub: "Trouvez les entreprises autour de vous en quelques secondes",
      icon: MapPin,
      to: "/terrain?tab=carte",
      testid: "find-maps",
      accent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      kpi: "Géo-radar",
    },
    {
      id: "import",
      title: "Importer ma liste",
      sub: "CSV, Excel ou vCard · vos contacts sont enrichis automatiquement",
      icon: Upload,
      to: "/import",
      testid: "find-import",
      accent: "bg-sky-500/15 text-sky-300 border-sky-500/40",
      kpi: "Bulk",
    },
    {
      id: "sector",
      title: "Recherche par secteur",
      sub: "Filtrez par activité, taille, zone — Prosperr scanne les bases publiques",
      icon: Search,
      to: "/pipeline?view=list&new=true",
      testid: "find-sector",
      accent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      kpi: "SIRENE",
    },
  ];

  return (
    <AppShell>
      <div className="px-5 pt-7 pb-4 pr-16">
        <div className="text-[10px] text-zinc-500 uppercase tracking-[0.3em] mb-2">Acquisition</div>
        <h1 className="h-editorial text-[34px] sm:text-[42px] text-[var(--text)]" data-testid="find-title">
          Nouveaux prospects.
        </h1>
        <p className="text-[12.5px] text-zinc-400 mt-2 leading-snug max-w-md">
          Choisissez la méthode qui correspond à votre prochaine action.
        </p>
        <div className="hairline-gradient mt-4 -mr-5" />
      </div>

      <div className="px-5 mt-1 space-y-3">
        {methods.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              onClick={() => navigate(m.to)}
              data-testid={m.testid}
              className="w-full bento-tile flex items-center gap-4 p-4 text-left lift-on-hover"
            >
              <div className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 border ${m.accent}`}>
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[14.5px] font-semibold text-[var(--text)]">{m.title}</div>
                  <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-zinc-500 border border-[var(--border-soft)] rounded-md px-1.5 py-0.5 shrink-0 tnum">{m.kpi}</span>
                </div>
                <div className="text-[12px] text-zinc-400 mt-1 leading-snug">{m.sub}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
            </button>
          );
        })}
      </div>

      {/* Pro tip strip — informe sans encombrer */}
      <div className="px-5 mt-6">
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-[var(--surface-alt)]/50 border border-[var(--border-soft)]">
          <Sparkles className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-[11.5px] text-zinc-400 leading-relaxed">
            <span className="text-zinc-200 font-medium">Astuce.</span>{" "}
            Une fois vos prospects ajoutés, Prosperr les enrichit automatiquement (SIRET, dirigeants, CA) via le bouton flottant en bas à droite.
          </div>
        </div>
      </div>

      <div className="h-20" />
      <MagicProspectButton />
    </AppShell>
  );
}
